/**
 * PositionCards (revised) — four cards, each with a per-card LINK
 * affordance that opens a bound drawer on the same page.
 *
 * Card → drawer mapping:
 *   Spending      → 'spending-breakdown'  ("What changed →")
 *   Money Kept    → 'briefing'            ("See progress →")
 *   Committed     → 'committed'           ("Review commitments →")
 *   Goal Progress → 'goal'                ("See goal →")
 */
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatMoney } from '@/lib/format';
import { ArrowUpRight, ArrowDownRight, ArrowRight } from 'lucide-react';

function Delta({ delta, positiveIsGood = false }) {
  if (delta == null || !isFinite(delta)) return null;
  if (Math.abs(delta) < 0.5) return <span className="text-[11px] text-muted-foreground">flat</span>;
  const up = delta > 0;
  const isGood = up === positiveIsGood;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${isGood ? 'text-emerald' : 'text-muted-warm'}`} data-testid="position-delta">
      <Icon className="h-3 w-3" />
      {Math.round(Math.abs(delta) * 10) / 10}%
    </span>
  );
}

function PositionCell({ title, value, subline, delta, positiveIsGood, linkLabel, drawerType, onOpenDrawer, loading, testid, unit = 'currency', baseCurrency, italic }) {
  return (
    <Card className="border-border/70 h-full" data-testid={testid}>
      <CardContent className="p-4 sm:p-5 flex flex-col h-full">
        <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{title}</div>
        {loading ? (
          <Skeleton className="h-7 w-28 mt-2" />
        ) : (
          <div className="mt-1.5 num text-2xl font-display font-semibold text-foreground">
            {value == null ? '—'
              : unit === 'percent' ? `${Math.round(value)}%`
              : unit === 'count'   ? String(value)
              : formatMoney(value, baseCurrency)}
          </div>
        )}
        <div className="mt-1.5 flex items-center gap-2">
          {!loading && <Delta delta={delta} positiveIsGood={positiveIsGood} />}
          {subline && <span className="text-[11px] text-muted-foreground truncate">{subline}</span>}
        </div>
        {italic && <p className="mt-2 text-xs text-muted-foreground italic">{italic}</p>}
        <div className="mt-auto pt-3">
          <button
            type="button"
            onClick={() => onOpenDrawer?.(drawerType)}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded"
            data-testid={`${testid}-link`}
          >
            {linkLabel}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PositionCards({
  loading = false,
  baseCurrency = 'INR',
  spending, moneyKept, committed, goal,
  onOpenDrawer,
}) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="dashboard-position-cards">
      <PositionCell
        title="Spending" value={spending?.value} delta={spending?.delta_pct}
        positiveIsGood={false} italic={spending?.italic}
        subline={spending?.delta_pct != null ? 'from last month' : 'this month'}
        linkLabel="What changed" drawerType="spending-breakdown"
        onOpenDrawer={onOpenDrawer}
        loading={loading} testid="position-card-spending" baseCurrency={baseCurrency}
      />
      <PositionCell
        title="Money kept" value={moneyKept?.value} delta={moneyKept?.delta_pct}
        positiveIsGood={true}
        subline={moneyKept?.delta_pct != null ? 'from last month' : 'this month'}
        linkLabel="See progress" drawerType="briefing"
        onOpenDrawer={onOpenDrawer}
        loading={loading} testid="position-card-kept" baseCurrency={baseCurrency}
      />
      <PositionCell
        title="Committed money" value={committed?.value}
        subline={committed?.pct_of_income != null ? `${Math.round(committed.pct_of_income)}% of monthly income` : (committed?.count != null ? `${committed.count} active` : 'monthly')}
        linkLabel="Review commitments" drawerType="committed"
        onOpenDrawer={onOpenDrawer}
        loading={loading} testid="position-card-committed" baseCurrency={baseCurrency}
      />
      <PositionCell
        title="Goal progress"
        value={goal?.progress_pct != null ? Math.round(goal.progress_pct) : null}
        subline={goal?.title || 'no active goal'}
        linkLabel="See goal" drawerType="goal"
        onOpenDrawer={onOpenDrawer}
        loading={loading} unit="percent"
        testid="position-card-goal" baseCurrency={baseCurrency}
      />
    </div>
  );
}
